import type { Dictionary } from "./dictionary";

export const en: Dictionary = {
  chartWeeklyDetailHint: "Point to a period to review its spending breakdown.",
  chartTrendDetailHint: "Point to a month to review its spending breakdown.",
  language: "Language",
  appTitle: "Family Weekly Budget",
  appSubtitle: "Weekly budget tracker",
  overviewAtGlance: "Your month at a glance",
  overviewHeadline: "Monthly overview",
  overviewFlowCopy: "Check the month, update the next period, then review the result.",
  overviewPeriodsTitle: "Your month in four checkpoints",
  overviewPeriodsSub: "Choose a period to review it or start the next update.",
  overviewImportTitle: "Update the next period in one step.",
  openImportFlow: "Import this period",
  overviewImportStepPaste: "Paste transactions",
  overviewImportStepReview: "Review categories",
  overviewImportStepSave: "Save the period",
  periodComplete: "Completed",
  periodNeedsUpdate: "Needs update",
  periodNotStarted: "Not started",
  titleUserFallback: "User",
  personalTitleSuffix: "Family Weekly Budget",
  periodControls: "Period",
  workspaceControls: "Workspace",
  systemControls: "System",
  month: "Month",
  selectMonth: "Select month",
  addMonth: "Add month",
  deleteMonth: "Delete current month",
  overview: "Overview",
  entry: "Weekly entry",
  history: "History",
  settings: "Settings",
  settingsWorkspaceTitle: "Account and data settings",
  settingsSectionProfile: "Profile",
  settingsSectionData: "Data",
  settingsSectionWorkspace: "Workspace",
  settingsSectionSecurity: "Security",
  settingsSectionAdmin: "Admin",
  mobileMore: "More",
  mobileSaveSummary: "Ready to save this period",
  currentMonth: "Current month",
  creditLimit: "Credit card limit",
  monthSpend: "Monthly spend to date",
  latestAvailable: "Latest available balance",
  latestWeekSpend: "Latest period spend",
  monthlyStatus: "Monthly status",
  statusOnTrack: "On track",
  statusWatch: "Watch",
  statusOver: "Over pace",
  statusNoData: "Waiting for data",
  statusOnTrackCopy: "Monthly spending is in line with or below last month's comparable pace.",
  statusWatchCopy: "Monthly spending is higher than last month's comparable pace. Watch the main drivers.",
  statusOverCopy: "Monthly spending is meaningfully higher than last month's comparable pace.",
  statusNoDataCopy: "Enter this week's figures to see the monthly readout.",
  spendingPace: (used, average, comparison) => `${used} used · avg ${average}/period · ${comparison}`,
  samePeriodLower: (change) => `${change} vs same period last month`,
  samePeriodHigher: (change) => `+${change} vs same period last month`,
  samePeriodUnavailable: "No same-period data last month",
  sameProgressLower: (change) => `${change} vs last month same progress`,
  sameProgressHigher: (change) => `+${change} vs last month same progress`,
  sameProgressUnavailable: "No same-progress data last month",
  lastMonthLower: (change) => `${change} vs last month`,
  lastMonthHigher: (change) => `+${change} vs last month`,
  lastMonthUnavailable: "No last-month data",
  selectedPeriodComparison: "Selected period comparison",
  periodComparisonLower: (change) => `-${change} vs same period last month`,
  periodComparisonHigher: (change) => `+${change} vs same period last month`,
  periodComparisonFlat: "No change vs same period last month",
  periodComparisonUnavailable: "No same-period data last month for this selected period.",
  latestPeriodChange: "Latest period change",
  comparedSamePeriod: "Compared with same period last month",
  largestDriver: "Notable spending this period",
  topDriversLine: (first, second) => (second ? `Notable spending: ${first} · ${second}` : `Notable spending: ${first}`),
  noDriverYet: "No notable spending this period",
  noPeriodDriverYet: "No notable spending this selected period",
  topMonthlyDriversLine: (first, second) =>
    second ? `Notable spending this month: ${first} · ${second}` : `Notable spending this month: ${first}`,
  noMonthlyDriverYet: "No notable spending this month",
  nextAction: "Next action",
  nextActionUpdate: "Update this week",
  nextActionReview: (driver) => `Review ${driver}`,
  nextActionWatch: "Watch the next period",
  openWeeklyEntry: "Open weekly entry",
  onboardingTitle: "Welcome",
  onboardingCopy: "Welcome. Add a month and enter your first weekly balance to see the monthly status.",
  emptyChartCopy: "Enter your first completed weekly balance to build this chart.",
  emptyWeeksCopy: "No completed weekly records yet.",
  weeklyComposition: "Period Spend Breakdown",
  weeklyCompositionSub: "Stacked non-grocery, grocery, and incidental spending",
  chartZoom: "Enlarge chart",
  chartRestore: "Restore chart",
  monthlyTrend: "Monthly Spending Trend",
  monthlyTrendSub: "Compare grocery, non-grocery, and incidental spending by month",
  historyCategoryChartTitle: "Monthly Category Breakdown",
  historyCategoryChartSub: "Spending by category; tile size and color depth show the amount",
  chartShareLabel: "Share",
  needTwoMonths: "At least two months are needed for a trend",
  monthlyTotal: "Monthly total",
  total: "Total",
  monthWeeks: "Month Period Records",
  period: "Period",
  to: "to",
  cumulative: "Monthly total",
  weeklyTotal: "Period total",
  nonGrocery: "Non-grocery",
  grocery: "Grocery",
  incidentals: "Incidentals",
  notes: "Notes",
  edit: "Edit",
  unnamedPeriod: "Untitled period",
  entryEyebrow: "Period editor",
  entryTitle: "Weekly Entry",
  saveWeek: "Save period",
  clearPeriod: "Clear period records",
  updateThisWeek: "Update this week",
  updateThisWeekSub: "Enter the weekly basics first, then review the calculated totals.",
  liveSummary: "Live summary",
  calculatedTotals: "Calculated totals",
  thisPeriodSpend: "This period spend",
  entryMonthSpend: "Month spend",
  liveSummaryCopy: "Totals update in the cards above as you enter the available balance and unpaid previous balance.",
  availableFormula: "Credit limit - available balance - unpaid previous balance",
  periodFormula: "Current monthly spend - previous period monthly spend",
  weekData: "Period data",
  weekDataSub: "Period total is calculated from the running monthly total",
  editPeriod: "Edit period",
  availableBalance: "Available balance",
  cumulativeAfterUnpaid: "Monthly spend after unpaid balance",
  unpaidPrevious: "Unpaid previous balance",
  categoryAmounts: "Category amounts",
  categoryAmountsSub: "Keep the full detailed category list; grocery spend is calculated automatically.",
  groceryExplainer: "Grocery is auto-calculated from period total minus detailed non-grocery and incidentals.",
  transactionImportTitle: "Import transactions",
  transactionImportSub: "Paste bank or card rows, review the draft, then apply confirmed rows to this period only.",
  currentPeriod: "Current period",
  pasteTransactions: "Paste transactions",
  parseTransactions: "Parse transactions",
  applyConfirmedRows: "Apply confirmed rows",
  includedRows: "Included",
  needsReviewRows: "Needs review",
  excludedRows: "Excluded",
  importSummaryLine: (count, amount) => `${count} transactions · ${amount}`,
  merchantDescription: "Merchant / description",
  suggestedCategory: "Suggested category",
  confidence: "Confidence",
  reason: "Reason",
  action: "Action",
  includeTransaction: "Include",
  noImportRows: "No transactions in this section.",
  importParseEmpty: "Paste transaction rows first.",
  importApplied: (count) => `Applied ${count} transactions to the current draft.`,
  importBalancesUpdated: "Updated available balance and unpaid amount from pasted text.",
  importNeedsPeriod: "Set Period start and Period end before importing.",
  importBalanceWarning: (importTotal, periodTotal) =>
    `Imported transaction total is ${importTotal}, but the current period spend from balances is ${periodTotal}. Check pending transactions, refunds, or date boundaries before saving.`,
  importGroceryWarning: (importGrocery, residualGrocery) =>
    `Imported grocery total is ${importGrocery}, while the current formula-derived grocery is ${residualGrocery}. Grocery will still use the existing formula.`,
  confidenceHigh: "High",
  confidenceMedium: "Medium",
  confidenceLow: "Low",
  reasonLabels: {
    "outside selected period": "outside selected period",
    "month not created": "month not created",
    "no matching existing period": "no matching existing period",
    "invalid date": "invalid date",
    "positive amount / payment / refund": "positive amount / payment / refund",
    "duplicate candidate": "duplicate candidate",
    "unsupported row format": "unsupported row format",
    "low confidence": "low confidence",
    "incidentals require confirmation": "incidentals require confirmation",
    "user excluded": "user excluded",
  },
  otherDetails: "Rare-event or incidental notes",
  incidentalsDetailsTitle: "Incidentals / rare-event notes",
  incidentalsDetailsSub: "Use only for unusual, unavoidable events.",
  incidentalsRareHint: "Rare, unavoidable events such as emergency repairs, sudden medical costs, or accident-related expenses.",
  searchEyebrow: "Search",
  pastRecords: "Past Records",
  historyOverviewTab: "Overview",
  historyTransactionsTab: "Transactions",
  allMonths: "All months",
  allCategories: "All categories",
  category: "Category",
  keyword: "Keyword",
  keywordPlaceholder: "Period, notes, category",
  merchant: "Merchant",
  merchantPlaceholder: "Search merchant or description",
  startDate: "Start date",
  endDate: "End date",
  allPeriods: "All periods",
  noTransactions: "No matching transactions",
  transactionWorkspaceScope: "Current workspace",
  transactionDate: "Transaction date",
  transactionContext: "Month / period",
  transactionContextLine: (month, period) => `${month} · ${period}`,
  notesPlaceholder: "e.g. emergency repair $amount\nunexpected fee $amount",
  minAmount: "Minimum amount",
  amount: "Amount",
  noRecords: "No matching records",
  dataSettingsTitle: "Data and Category Settings",
  monthData: "Current month data",
  monthDataSub: "These settings are saved to the backend data file",
  saveSettings: "Save settings",
  monthName: "Month name",
  backup: "Data backup",
  backupSub: "Export current data or import data generated by a model/backup",
  exportJson: "Export JSON",
  importJson: "Import JSON",
  categorySettings: "Category Settings",
  categorySettingsSub: "System categories stay stable; workspace categories can be added, renamed, or archived.",
  type: "Type",
  ruleHint: "Rule hint",
  categoryName: "Category name",
  categoryNamePlaceholder: "e.g. Pets",
  categoryType: "Category group",
  categoryHint: "Category description",
  categoryHintPlaceholder: "e.g. veterinary, food, grooming",
  addCategory: "Add category",
  categoryStatus: "Status",
  categoryActiveStatus: "Active",
  categoryArchivedStatus: "Archived",
  categorySystemStatus: "System",
  categoryEdit: "Edit",
  categoryArchive: "Archive",
  categoryRestore: "Restore",
  categoryAdded: "Category added.",
  categorySaved: "Category saved.",
  categoryArchived: "Category archived.",
  categoryRestored: "Category restored.",
  categoryNameInvalid: "Enter a category name with at least 2 characters.",
  categoryEditTitle: "Edit category",
  categoryEditNamePrompt: "Update the category name.",
  categoryEditHintPrompt: "Update the category description.",
  categoryArchiveTitle: "Archive category",
  categoryArchiveConfirm: (category) => `Archive "${category}"? Existing records will remain visible.`,
  autoCalculated: "Auto calculated",
  incidentalType: "Incidental",
  categoryLabels: {
    medical: "Medical out-of-pocket",
    privateInsurance: "Private insurance",
    electricity: "Electricity",
    gas: "Gas",
    internetMobile: "Internet, mobile, subscriptions",
    water: "Water",
    school: "School fees",
    homeInsurance: "Home insurance",
    carInsurance: "Car insurance",
    transport: "Transport",
    government: "Government fees",
    shoppingDining: "Shopping and dining",
    incidentals: "Incidentals",
  },
  deleteOnlyMonth: "Keep at least one month.",
  deleteConfirm: (name) => `Delete "${name}" and all period records in it?`,
  clearPeriodConfirm: (period) => `Clear all records for "${period}"? This also removes category amounts, notes, and transactions.`,
  periodCleared: (period) => `Cleared all records for ${period}.`,
  importFailed: "Import failed. Please check the JSON format.",
  dialogNoticeTitle: "Notice",
  dialogConfirmTitle: "Please confirm",
  dialogOk: "OK",
  dialogConfirm: "Confirm",
  createWorkspaceTitle: "New workspace",
  firstPeriod: "Period 1",
  secondPeriod: "Period 2",
  thirdPeriod: "Period 3",
  fourthPeriod: "Period 4",
  buildVersion: "Build version",
  editingPeriod: (period) => `Editing ${period}`,
  editingExistingPeriod: "Editing an existing period. Saving will replace these values.",
  periodTimelineWarning:
    "Overlapping or missing date ranges were detected. Saving is still allowed, but you need to review and fix the period timeline.",
  saveSuccess: "Weekly entry saved",
  saveSuccessDetailed: (period, total, grocery) => `Saved ${period}: ${total} period total, ${grocery} grocery.`,
  loginRequired: "Login required",
  loginTitle: "Enter password",
  loginSub: "This family budget is password-protected. Ask the household budget owner for access.",
  checkingAccess: "Checking access",
  checkingAccessTitle: "Preparing your workspace...",
  loginAccountId: "Account ID (optional)",
  password: "Password",
  login: "Log in",
  logout: "Log out",
  cancel: "Cancel",
  loginFailed: "Incorrect password. Please try again.",
  workspace: "Workspace",
  workspaceSwitching: "Switching...",
  workspaceSwitched: "Workspace switched",
  workspaceSwitchFailed: "Unable to switch workspace. Please try again.",
  createWorkspace: "New workspace",
  createWorkspacePrompt: "Enter a name for the new workspace",
  createWorkspaceFailed: "Unable to create workspace. Please try again.",
  workspaceManagementTitle: "Workspace management",
  workspaceManagementSub: "Rename or delete workspaces owned by this account.",
  workspaceActions: "Workspace actions",
  today: "Today",
  guidedIntroMonthMenuTitle: "Step 1 — Choose a month",
  guidedIntroMonthMenuBody: "Open the three-dot month menu to add or remove months. Adding a month creates the weekly slots for your next update.",
  guidedIntroImportTitle: "Step 3 — Import the next period",
  guidedIntroImportBody: "Start here for each update. Paste your bank or card rows, review the categories, then save the confirmed period.",
  renameWorkspace: "Rename workspace",
  renameWorkspaceTitle: "Rename workspace",
  renameWorkspacePrompt: "Enter a new name for this workspace.",
  deleteWorkspace: "Delete workspace",
  workspaceRenameSuccess: "Workspace renamed.",
  workspaceRenameFailed: "Unable to rename workspace. Check the name and try again.",
  workspaceDeleteConfirm: (name) => `Delete "${name}"? This permanently removes this workspace's data.`,
  workspaceDeleteSuccess: "Workspace deleted.",
  workspaceDeleteFailed: "Unable to delete workspace. Confirm it is not the default or final workspace.",
  accountAdminTitle: "Account management",
  accountAdminSub: "Create internal password accounts and manage Google trial user access.",
  accountId: "Account ID",
  displayName: "Display name",
  emailOptional: "Email (optional)",
  workspaceName: "Workspace name",
  temporaryPassword: "Temporary password",
  createAccount: "Create account",
  accountCreateSaving: "Creating...",
  accountCreateSuccess: (account, workspace) => `Created ${account} with workspace ${workspace}.`,
  accountCreateFailed: "Unable to create account. Check the details and try again.",
  accountCreateDuplicate: "This account ID already exists. Choose another ID.",
  accountLoginHint: "Share the temporary password securely. The app will not show or store plaintext passwords.",
  managedUsersTitle: "Users",
  managedUsersSub: "Review Google trial users and promote approved users to standard access.",
  managedUsersEmpty: "No other users yet.",
  promoteToStandard: "Promote to standard",
  accountPromoteSuccess: (account) => `Promoted ${account} to standard access.`,
  accountPromoteFailed: "Unable to update user status. Please try again.",
  deleteAccount: "Delete account",
  accountDeleteConfirm: (account) => `Delete "${account}"? This permanently removes this user's workspaces and server data.`,
  accountDeleteSuccess: (account) => `Deleted ${account} and owned workspaces.`,
  accountDeleteFailed: "Unable to delete account. Please try again.",
  noActionNeeded: "No action needed",
  accountResetTitle: "Admin password reset",
  accountResetSub: "Set a new temporary password when a password account needs recovery access.",
  accountToReset: "Account to reset",
  newTemporaryPassword: "New temporary password",
  confirmTemporaryPassword: "Confirm temporary password",
  resetAccountPassword: "Reset password",
  accountResetNoAccounts: "No other password accounts are available to reset.",
  accountResetSaving: "Resetting...",
  accountResetSuccess: (account) => `Reset password for ${account}.`,
  accountResetFailed: "Unable to reset password. Check the account and new password.",
  accountSecurityTitle: "Account security",
  accountSecuritySub: "Change the password for the account currently signed in.",
  profileTitle: "Profile",
  profileSub: "Update the display name shown in the application.",
  guidedIntroHelpTitle: "Guided intro",
  guidedIntroHelpSub: "The first-time walkthrough appears automatically once. You can replay it here any time.",
  guidedIntroReplay: "Replay guided intro",
  guidedIntroLabel: "Guided intro",
  guidedIntroBack: "Back",
  guidedIntroNext: "Next",
  guidedIntroFinish: "Finish",
  guidedIntroSkip: "Skip for now",
  guidedIntroProgress: (current, total) => `Step ${current} / ${total}`,
  guidedIntroWelcomeTitle: (name) => `Welcome, ${name}`,
  guidedIntroWelcomeBody:
    "This is your family budget tracker. Spend 2 minutes a week entering spending — the system builds your charts and analysis automatically. Follow the 5 steps below to get started.",
  guidedIntroStep2Title: "Step 1 — Add a month",
  guidedIntroStep2Body:
    "Click Add month to set up the current month. This creates a weekly slot for each week of the month, ready for you to fill in.",
  guidedIntroStep3Title: "Step 2 — Set your credit card limit",
  guidedIntroStep3Body:
    "In Settings → Data, enter your credit card total limit. This is the baseline the system uses to calculate available balance and monthly spend.",
  guidedIntroStep4Title: "Step 3 — Log weekly spending",
  guidedIntroStep4Body:
    "Click Weekly entry above and fill in your spending totals by category. Once saved, the overview immediately shows your monthly status — whether you're on track or overspending.",
  guidedIntroStep5Title: "Step 4 — Monthly statement",
  guidedIntroStep5Body:
    "After you save weekly data, this card summarises your monthly status at a glance — whether spending is on track and which categories need attention.",
  guidedIntroStep6Title: "Step 5 — Trend chart",
  guidedIntroStep6Body:
    "After 2–3 months of weekly entries, the trend chart builds automatically. You don't have data yet — keep logging for a few weeks and the chart will appear here.",
  guidedIntroFinishTitle: "You're all set!",
  guidedIntroFinishBody:
    "The Settings tab also has import/export so you can sync data between family members or devices. Now add this month and log your first week!",
  guidedIntroEntryTitle: "Start with weekly entry",
  guidedIntroEntryBody:
    "Each update begins here. Enter the weekly available balance, any unpaid previous amount, and category totals to create the week's record.",
  guidedIntroStatusTitle: "Monthly status is the main summary",
  guidedIntroStatusBody:
    "After you save weekly data, the overview turns it into a simple monthly reading so you can quickly tell whether spending is still on track.",
  guidedIntroChartsTitle: "Charts are built from your entries",
  guidedIntroChartsBody:
    "The weekly and monthly visuals are generated from the records you enter, so better weekly data leads to clearer month-level insight.",
  guidedIntroWorkspaceTitle: "Workspace is your budget context",
  guidedIntroWorkspaceBody:
    "Use different workspaces for different families, scenarios, or testing. Switching workspace changes which dataset you are viewing.",
  guidedIntroSettingsTitle: "Settings is your control area",
  guidedIntroSettingsBody:
    "Use settings for language, display name, security actions, and replaying this guided intro later if you want a refresher.",
  currentPassword: "Current password",
  newPassword: "New password",
  confirmNewPassword: "Confirm new password",
  changePassword: "Change password",
  passwordChangeSuccess: "Password updated. Use the new password next time you sign in.",
  passwordChangeFailed: "Unable to update password. Check the current password and new password.",
  passwordMismatch: "New password and confirmation do not match.",
  signedInAccount: "Signed-in account",
  signInMethod: "Sign-in method",
  accountStatus: "Account status",
  trialAccount: "Trial",
  activeAccount: "Standard",
  continueWithGoogle: "Continue with Google",
  orPasswordLogin: "or use password login",
  landingEyebrow: "Private trial release",
  landingHeadline: "Weekly budgeting, clearly presented.",
  landingCopy: "A simple private workspace for monthly oversight, weekly entry, and transaction review.",
  landingStartTrialAccess: "Start trial access",
  landingStartWithGoogle: "Start with Google",
  landingContactUs: "Contact us",
  landingPillStatement: "Monthly statement",
  landingPillTrend: "Trend graph",
  landingPillReview: "Transaction review",
  landingHighlightsAria: "Product highlights",
  landingVisualAria: "Real product screenshots",
  landingStatementImageAlt: "Family Weekly Budget monthly statement highlight",
  landingTrendImageAlt: "Family Weekly Budget monthly spending trend graph highlight",
  landingTrialEyebrow: "Trial access",
  landingTrialTitle: "Start in three steps",
  landingStep1Title: "Request a password",
  landingStep1Copy: "Ask the service owner or demo host for temporary access.",
  landingStep2Title: "Sign in",
  landingStep2Copy: "Use the shared password to open the workspace.",
  landingStep3Title: "Review the workflow",
  landingStep3Copy: "See overview, entry, and transaction import in a single flow.",
  landingContactEyebrow: "Contact us",
  landingContactTitle: "Trial access and onboarding",
  landingContactCopy:
    "Trial access is currently provided by invitation. If you are reviewing the product with a team, contact your service owner or demo host to arrange access and onboarding.",
  landingContactAction: "Request trial access",
  landingContactEmailBtn: "Send us an email",
  landingRequestTrialAccess: "Request trial access",
  landingPrivateWorkspace: "Private workspace",
  landingLoginTitle: "Log in to continue",
  landingAuthCopySignedIn: "You are already signed in. Continue directly to the workspace.",
  landingAuthCopyGoogle: "Continue with Google to create or open your trial workspace.",
  landingAuthCopyOpen: "This preview is currently open. Continue directly to the workspace.",
  landingAuthCopyPassword: "Continue to the private workspace. You will enter your password on the secure app login screen.",
  landingAuthFootnoteGoogle: "Google trial users get a clean workspace automatically. Password login remains available for existing accounts.",
  landingAuthFootnotePassword: "Password login is reserved for existing owner or internal accounts.",
  landingAuthFootnoteOpen: "Trial access is open in this environment. You can enter the workspace directly.",
  landingOpenWorkspace: "Open workspace",
  merchantRulesTitle: "Merchant category memory",
  merchantRulesSub: "Manually saved merchant-to-category rules. These take priority over system defaults. Scoped to this workspace only.",
  merchantRulesAdd: "Add merchant rule",
  merchantRulesMerchantPlaceholder: "e.g. TPG INTERNET",
  merchantRulesMerchantInvalid: "Enter a merchant name.",
  merchantRulesDuplicate: "A rule already exists for this merchant.",
  merchantRulesSaved: "Merchant rule saved.",
  merchantRulesEmpty: "No merchant rules yet. Change a transaction category during import and choose \"Remember\" to create one.",
  merchantRulesMerchant: "Merchant",
  merchantRulesCategory: "Category",
  merchantRulesAdded: "Added",
  merchantRulesDelete: "Delete",
  merchantRulesDeleteConfirm: "Delete this merchant rule?",
  merchantRulesDeleteConfirmTitle: "Delete merchant rule",
  merchantRulesUpdated: "Merchant rule updated",
  merchantRulesDeleted: "Merchant rule deleted",
  merchantRulesRememberTitle: "Remember merchant category",
  merchantRulesRememberMessage: (merchant, category) => `Always categorise "${merchant}" as "${category}"? (This workspace only)`,
  merchantRulesRemember: "Remember this choice",
  merchantRulesOnlyThis: "Only this transaction",
  merchantRulesUpdateTitle: "Update merchant rule",
  merchantRulesUpdateMessage: (merchant, oldCat, newCat) => `Update saved rule for "${merchant}" from "${oldCat}" to "${newCat}"?`,
  merchantRulesUpdate: "Update rule",
  merchantRulesSource: "Source",
  merchantRulesSourceUser: "User",
  merchantRulesSourceSystem: "System",
  entryUnsavedTitle: "Unsaved changes",
  entryUnsavedConfirm: "You have unsaved changes. Are you sure you want to switch weeks? Unsaved data will be lost.",
};
